import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { TrailWindowValue } from '@/constants/monitor'
import type { LiveStatus } from '@/constants/monitor-status'
import { getDayRange } from '@/lib/dates'

interface DateRange {
    startDate: Date
    endDate: Date
}

interface CardsViewSlice {
    statusFilter: LiveStatus | 'working' | 'off' | 'late' | null
    setStatusFilter: (filter: LiveStatus | 'working' | 'off' | 'late' | null) => void
}

interface UISlice {
    shouldAutoUpdateView: boolean
    setShouldAutoUpdateView: (enabled: boolean) => void
    zoomLevel: number | null
    setZoomLevel: (level: number) => void
    mapHeading: number
    setMapHeading: (heading: number) => void
}

interface DateTimeSlice {
    dateRange: DateRange
    setDateRange: (range: DateRange) => void
}

interface MomentSlice {
    selectedMoment: Date | null
    setSelectedMoment: (at: Date | null) => void
}

interface VisibilitySlice {
    userVisibility: Record<string, boolean>
    toggleUser: (userId: string) => void
    toggleAllUsers: (userIds: string[], visible: boolean) => void
    initializeVisibility: (userIds: string[]) => void
}

interface LocationSelectionSlice {
    selectedLocationId: string | null
    setSelectedLocation: (locationId: string | null) => void
}

interface TrailSettingsSlice {
    trailWindow: TrailWindowValue
    setTrailWindow: (minutes: TrailWindowValue) => void
}

interface MonitorStore
    extends CardsViewSlice,
        UISlice,
        DateTimeSlice,
        MomentSlice,
        VisibilitySlice,
        LocationSelectionSlice,
        TrailSettingsSlice {}

const getInitialDateRange = (): DateRange => {
    const today = getDayRange(new Date())
    return {
        startDate: today.startDate,
        endDate: new Date(),
    }
}

export const useMonitorStore = create<MonitorStore>()(set => ({
    // Cards View Slice
    statusFilter: null,
    setStatusFilter: filter => set({ statusFilter: filter }),

    // UI Slice
    shouldAutoUpdateView: true,
    setShouldAutoUpdateView: enabled => set({ shouldAutoUpdateView: enabled }),
    zoomLevel: null,
    setZoomLevel: level => set({ zoomLevel: level }),
    mapHeading: 0,
    setMapHeading: heading => set({ mapHeading: heading }),

    // DateTime Slice
    dateRange: getInitialDateRange(),
    setDateRange: range => set({ dateRange: range }),

    // Moment Slice
    selectedMoment: null,
    setSelectedMoment: at => set({ selectedMoment: at }),

    // Visibility Slice
    userVisibility: {},
    toggleUser: userId =>
        set(state => ({
            userVisibility: {
                ...state.userVisibility,
                [userId]: !(state.userVisibility[userId] ?? true),
            },
        })),
    toggleAllUsers: (userIds, visible) =>
        set(() => {
            const newVisibility: Record<string, boolean> = {}
            for (const id of userIds) {
                newVisibility[id] = visible
            }
            return { userVisibility: newVisibility }
        }),
    initializeVisibility: userIds =>
        set(state => {
            const hasNewUsers = userIds.some(id => !(id in state.userVisibility))
            if (!hasNewUsers) return state
            const newVisibility = { ...state.userVisibility }
            for (const id of userIds) {
                if (!(id in newVisibility)) {
                    newVisibility[id] = true
                }
            }
            return { userVisibility: newVisibility }
        }),

    // Location Selection Slice
    selectedLocationId: null,
    setSelectedLocation: locationId => set({ selectedLocationId: locationId }),

    // Trail Settings Slice
    trailWindow: null,
    setTrailWindow: minutes => set({ trailWindow: minutes }),
}))

export const useMonitorCardsView = () =>
    useMonitorStore(
        useShallow(state => ({
            statusFilter: state.statusFilter,
            setStatusFilter: state.setStatusFilter,
        }))
    )

export const useMonitorUIState = () =>
    useMonitorStore(
        useShallow(state => ({
            setShouldAutoUpdateView: state.setShouldAutoUpdateView,
        }))
    )

export const useMonitorDateTimeState = () =>
    useMonitorStore(
        useShallow(state => ({
            dateRange: state.dateRange,
            setDateRange: state.setDateRange,
        }))
    )

export const useIsViewingToday = () =>
    useMonitorStore(state => {
        return state.dateRange.startDate.toDateString() === new Date().toDateString()
    })

export const useMonitorMoment = () =>
    useMonitorStore(
        useShallow(state => ({
            selectedMoment: state.selectedMoment,
            setSelectedMoment: state.setSelectedMoment,
        }))
    )

export const useSelectedLocation = () =>
    useMonitorStore(
        useShallow(state => ({
            selectedLocationId: state.selectedLocationId,
            setSelectedLocation: state.setSelectedLocation,
        }))
    )

export const useMapZoom = () =>
    useMonitorStore(
        useShallow(state => ({
            zoomLevel: state.zoomLevel,
            setZoomLevel: state.setZoomLevel,
        }))
    )

export const useMapHeading = () =>
    useMonitorStore(
        useShallow(state => ({
            mapHeading: state.mapHeading,
            setMapHeading: state.setMapHeading,
        }))
    )

export const useTrailSettings = () =>
    useMonitorStore(
        useShallow(state => ({
            trailWindow: state.trailWindow,
            setTrailWindow: state.setTrailWindow,
        }))
    )
